import React, { useEffect, useState } from "react";
import { Button, Box, useColorModeValue, Select, Flex, Heading } from "@chakra-ui/react";
import { db } from "../firebase.config";
import { collection, getDocs, query, where } from "firebase/firestore";
import { Link, useNavigate } from "react-router-dom";
import FadeSpinner from "../components/feedback/FadeSpinner";
import ListingItem from "../components/listings/ListingItem";
import { ImLocation, ImSortAmountAsc, ImSortAmountDesc } from "react-icons/im";
import moment from "moment";
import { Text, Badge } from "@chakra-ui/react";
import { Helmet } from "react-helmet";

const SeeAllFeaturedCars = () => {
  const [cars, setCars] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sortOrder, setSortOrder] = useState("asc");
  const [error, setError] = useState("");
  const navigate = useNavigate();

  const cardBg = useColorModeValue("white", "gray.800");
  const shadowColor = useColorModeValue(
    "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
    "0 4px 6px -1px rgba(0, 0, 0, 0.3), 0 2px 4px -1px rgba(0, 0, 0, 0.2)"
  );

  useEffect(() => {
    const fetchCars = async () => {
      setLoading(true);
      try {
        const carsQuery = query(
          collection(db, "cars"),
          where("featured", "==", true)
        );
        const carQuerySnapshot = await getDocs(carsQuery);
        const carListings = carQuerySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));

        // Sort cars by ranking based on sortOrder state
        const sortedCars = carListings.sort((a, b) =>
          sortOrder === "asc" ? a.ranking - b.ranking : b.ranking - a.ranking
        );
        setCars(sortedCars);
        setLoading(false);
      } catch (err) {
        console.error("Error fetching car listings:", err);
        setError("Failed to fetch car listings.");
        setLoading(false);
      }
    };

    fetchCars();
  }, [sortOrder]);

  const checkDetails = (id) => {
    navigate(`/category/sale/${id}`);
  };

  if (loading) return <FadeSpinner />;
  if (error) return <Box className="text-center text-red-500 p-8">{error}</Box>;
  if (cars.length === 0) return (
    <Box className="text-center p-8">
      <Heading size="lg" className="text-gray-600 mb-4">No featured cars available</Heading>
      <Link to="/" className="text-blue-500 hover:underline">Browse other vehicles</Link>
    </Box>
  );

  return (
    <>
      <Helmet>
            <title>Featured Cars | NCP Wheels</title>
            <meta
              name="keywords"
              content="ncp cars, non custom cars, ncp car, non custom paid cars, ncp cars for sale in swat, ncp cars for sale in swat pakistan, ncp car for sale in swat, ncp cars for sale in pakistan, ncp cars in pakistan for sale, ncp car for sale in pakistan, pakistan non custom cars for sale, non custom car for sale in pakistan, non custom cars in pakistan for sale, non custom cars for sale in pakistan, ncp car for sale, ncp cars for sale in swat olx, ncp cars for sale in chaman, ncp cars in swat, non custom paid cars in chaman, chaman border cars, ncp cars swat, swat ncp cars, non custom paid cars chaman, non custom cars in chaman, chaman non custom cars, chaman border non custom cars price, ncp cars in pakistan, cars for sale in swat, ncp cars swat chakdara, ncp swat chakdara motors, car for sale in swat, chaman border cars for sale in pakistan, non custom car price in chaman"
            />
          </Helmet>
    <div className="min-h-screen bg-gray-50 p-4 md:p-8 md:mx-[10%]">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-8">
          <Heading as="h1" size="xl" className="text-gray-800 mb-2">
            Featured Luxury Cars
          </Heading>
          <div className="w-24 h-1 bg-blue-500 mx-auto mb-6"></div>
          
          <Flex justify="center" align="center" mb-6>
            <Box mr={2}>
              {sortOrder === "asc" ? <ImSortAmountAsc size={20} /> : <ImSortAmountDesc size={20} />}
            </Box>
            <Select
              value={sortOrder}
              onChange={(e) => setSortOrder(e.target.value)}
              width="200px"
              borderRadius="md"
              borderColor="blue.300"
            >
              <option value="asc">Sort: Low to High</option>
              <option value="desc">Sort: High to Low</option>
            </Select>
          </Flex>
        </div>

        <div className="grid grid-cols-1 gap-6 md:gap-8">
          {cars.map((car) => (
            <React.Fragment key={car.id}>
              {/* Desktop View */}
              <Box className="hidden md:block">
                <ListingItem 
                  id={car.id} 
                  data={car} 
                  editListing={false} 
                  deleteListing={false} 
                />
              </Box>
              
              {/* Mobile View */}
              <Box 
                className={`md:hidden bg-${cardBg} rounded-xl overflow-hidden border border-opacity-20 border-gray-300 transition-all duration-300 transform hover:-translate-y-1 hover:shadow-lg`}
                style={{ boxShadow: shadowColor }}
              >
                <div className="relative group overflow-hidden h-48">
                  <img
                    src={car.carImages?.[0] || "/placeholder-car.jpg"}
                    alt={car.modelName}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  
                  <div className="absolute top-3 right-3 flex space-x-2">
                    {car.offer && (
                      <span className="bg-red-500 text-white text-xs px-2 py-1 rounded-full shadow-md">
                        Special Offer
                      </span>
                    )}
                    <span className="bg-blue-500 text-white text-xs px-2 py-1 rounded-full shadow-md">
                      Featured
                    </span>
                  </div>
                </div>

                <div className="p-5">
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <Heading as="h3" size="md" className="text-gray-900">
                        {car.modelName} {car.modelYear}
                      </Heading>
                      <Text className="text-blue-600">{car.manufacturer}</Text>
                    </div>
                    {car?.timestamp && (
                      <Badge colorScheme="green" fontSize="xs">
                        {moment(car?.timestamp?.toDate()).fromNow()}
                      </Badge>
                    )}
                  </div>

                  <div className="flex items-center text-gray-600 mb-3">
                    <ImLocation className="inline mr-1 text-blue-500" size={14} />
                    <Text fontSize="sm">{car.location || car.city}</Text>
                  </div>

                  <div className="grid grid-cols-2 gap-2 mb-4">
                    <Box className="bg-blue-50 p-2 rounded-lg">
                      <Text fontSize="sm" className="text-gray-700">
                        <span className="font-semibold">Mileage:</span> {car.mileage || 'N/A'} Km
                      </Text>
                    </Box>
                    <Box className="bg-blue-50 p-2 rounded-lg">
                      <Text fontSize="sm" className="text-gray-700">
                        <span className="font-semibold">Color:</span> {car.color || 'N/A'}
                      </Text>
                    </Box>
                  </div>

                  <div className="flex justify-between items-center mb-4">
                    <Text fontSize="lg" fontWeight="bold" className="text-blue-700">
                      PKR {car.offer ? car.discountedPrice : car.price}
                    </Text>
                    {car.offer && (
                      <Text fontSize="sm" className="line-through text-gray-500">
                        PKR {car.price}
                      </Text>
                    )}
                  </div>

                  <Button
                    onClick={() => checkDetails(car.id)}
                    width="full"
                    className="transition-all duration-300 transform hover:scale-[1.02] hover:shadow-md"
                    bgGradient="linear(to-r, blue.500, blue.600)"
                    color="white"
                    _hover={{
                      bgGradient: "linear(to-r, blue.600, blue.700)",
                      transform: "translateY(-1px)",
                      boxShadow: "md",
                    }}
                    _active={{
                      bgGradient: "linear(to-r, blue.700, blue.800)",
                    }}
                    size="md"
                    borderRadius="lg"
                    boxShadow="sm"
                  >
                    View Details
                  </Button>
                </div>
              </Box>
            </React.Fragment>
          ))}
        </div>
      </div>
    </div>
    </>

  );
};

export default SeeAllFeaturedCars;